import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useFieldArray, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { toast } from 'sonner';
import { ChevronDown, ChevronUp, Loader2, Plus, Trash2, X } from 'lucide-react';
import { Sheet, SheetContent, SheetDescription, SheetTitle } from '../ui/sheet';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Form, FormControl, FormDescription, FormField, FormItem, FormLabel, FormMessage } from '../ui/form';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import ConfirmDialog from '../ui/ConfirmDialog';
import AutoSaveIndicator from '../ui/AutoSaveIndicator';
import { useAutoSave } from '../../hooks/useAutoSave';
import { Category } from '../../types';
import { TeamRow } from '../../hooks/useTeams';

// Édition d'une équipe dans un panneau latéral (2026-09-23), et non plus dans
// une modale : la liste reste visible, on sait quelle équipe on édite, et
// ↑ ↓ passe à la voisine sans refermer.
//
// La validation est déclarée ici (zod) et chaque erreur s'affiche SOUS le
// champ fautif — l'ancien écran disait « données invalides » dans une alerte
// en tête, sans dire lequel.
//
// SAUVEGARDE AUTOMATIQUE (2026-09-27, règle du 22/09) : sur une équipe qui
// existe, nom, code, contact et participants s'écrivent au blur si la valeur a
// changé et passe la validation ; retirer un participant s'écrit tout de suite
// avec « Annuler » sur le message. Une seule chose garde un geste explicite :
// la CATÉGORIE d'une équipe qui a payé, parce qu'elle écrit chez quelqu'un
// d'autre (complément ou remboursement Stripe, email à l'athlète) — elle passe
// par la fenêtre d'impact de la page. Une équipe non payée change de catégorie
// au choix, avec « Annuler ». La création garde son bouton : sans ligne en
// base, écrire au blur créerait une équipe fantôme par panneau abandonné.

export interface TeamFormValues {
  name: string;
  team_id: string;
  category_id: string;
  contact_email: string;
  contact_phone: string;
  participants: { first_name: string; last_name: string; email: string; phone: string }[];
}

export type Participant = TeamFormValues['participants'][number];

/** Ce que `PUT /teams/{id}` accepte (sémantique partielle : clé absente = préservée). */
export interface TeamPatch {
  team_name?: string;
  team_id?: string;
  category_id?: string;
  contact_email?: string;
  contact_phone?: string;
  participants?: Participant[];
}

const emptyParticipant: Participant = { first_name: '', last_name: '', email: '', phone: '' };

/** Les participants tels qu'ils partent au serveur : nommés, trim, sans les lignes vides. */
export const participantsPayload = (list: Participant[]): Participant[] => list
  .map((p) => ({ ...p, first_name: p.first_name.trim(), last_name: p.last_name.trim(), email: p.email.trim() }))
  .filter((p) => p.first_name && p.last_name);

function toValues(team: TeamRow | null): TeamFormValues {
  if (!team) {
    return { name: '', team_id: '', category_id: '', contact_email: '', contact_phone: '', participants: [{ ...emptyParticipant }] };
  }
  return {
    name: team.name || '',
    team_id: team.team_id || '',
    category_id: team.category_id || '',
    contact_email: team.contact_email || '',
    contact_phone: team.contact_phone || '',
    participants: team.participantList.length ? team.participantList.map((p) => ({ ...p })) : [{ ...emptyParticipant }],
  };
}

interface Props {
  open: boolean;
  /** null = création. */
  team: TeamRow | null;
  categories: Category[];
  /** Codes d'équipe déjà pris dans l'événement (doublon refusé à la saisie). */
  takenCodes: string[];
  saving: boolean;
  serverError: string;
  heatSummary?: string;
  /** Création seulement. */
  onSubmit: (values: TeamFormValues) => void;
  /** Édition : une écriture partielle, immédiate. Lève sur refus du serveur. */
  onAutoSave: (patch: TeamPatch) => Promise<void>;
  /**
   * Édition : changement de catégorie. La page décide du chemin (fenêtre
   * d'impact si l'équipe a payé, écriture directe sinon) et rend `true` si la
   * nouvelle catégorie est acquise, `false` si rien n'a été écrit (fenêtre
   * ouverte ou refus) — le sélecteur revient alors à l'ancienne valeur.
   */
  onCategoryChange: (newCategoryId: string) => Promise<boolean>;
  onClose: () => void;
  onDirtyChange: (dirty: boolean) => void;
  onPrev?: () => void;
  onNext?: () => void;
}

export function TeamSheet({
  open, team, categories, takenCodes, saving, serverError, heatSummary,
  onSubmit, onAutoSave, onCategoryChange, onClose, onDirtyChange, onPrev, onNext,
}: Props) {
  const { t } = useTranslation();
  const editing = !!team;

  const schema = useMemo(() => {
    const participant = z.object({
      first_name: z.string(),
      last_name: z.string(),
      email: z.string(),
      phone: z.string(),
    }).superRefine((p, ctx) => {
      const named = p.first_name.trim() || p.last_name.trim();
      if (!named) return;
      if (!p.email.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email'], message: t('teams.form.participantEmailRequired') });
      } else if (!z.string().email().safeParse(p.email.trim()).success) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: ['email'], message: t('teams.form.emailInvalid') });
      }
      if (!p.first_name.trim() || !p.last_name.trim()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, path: [p.first_name.trim() ? 'last_name' : 'first_name'], message: t('teams.form.fullNameRequired') });
      }
    });
    return z.object({
      name: z.string().trim().min(1, t('teams.form.nameRequired')),
      team_id: z.string().refine((v) => !v || !takenCodes.includes(v), (v) => ({ message: t('teams.duplicateId', { id: v }) })),
      category_id: z.string(),
      contact_email: z.string().refine((v) => !v.trim() || z.string().email().safeParse(v.trim()).success, t('teams.form.emailInvalid')),
      contact_phone: z.string(),
      participants: z.array(participant),
    });
  }, [t, takenCodes]);

  const form = useForm<TeamFormValues>({
    resolver: zodResolver(schema),
    defaultValues: toValues(team),
    mode: 'onTouched',
  });
  const { fields, append, remove, insert } = useFieldArray({ control: form.control, name: 'participants' });

  const autoSave = useAutoSave<TeamPatch>(onAutoSave, {
    onError: (err: any) => toast.error(err?.response?.data?.detail || t('errors.generic')),
  });
  // Les participants se comparent par contenu, pas par référence : un blur qui
  // n'a rien changé ne doit pas réécrire la liste.
  const participantsBaseline = useRef('');

  // Changer d'équipe (↑ ↓, clic sur une autre ligne) repart de ses valeurs,
  // et c'est cet état-là que l'auto-save prend pour référence.
  useEffect(() => {
    const values = toValues(team);
    form.reset(values);
    autoSave.setBaseline({
      team_name: values.name, team_id: values.team_id, category_id: values.category_id,
      contact_email: values.contact_email, contact_phone: values.contact_phone,
    });
    participantsBaseline.current = JSON.stringify(participantsPayload(values.participants));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.id, open]);

  // La catégorie n'est jamais tapée : quand la page l'a changée (fenêtre
  // d'impact validée), le sélecteur suit sans toucher au reste du formulaire.
  useEffect(() => {
    if (team) form.setValue('category_id', team.category_id || '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [team?.category_id]);

  // En édition rien n'attend jamais : le garde « non enregistré » ne sert
  // qu'à la création.
  const isDirty = !editing && form.formState.isDirty;
  useEffect(() => { onDirtyChange(isDirty); }, [isDirty, onDirtyChange]);

  const [confirmDiscard, setConfirmDiscard] = useState<null | (() => void)>(null);
  const guard = (action: () => void) => (isDirty ? setConfirmDiscard(() => action) : action());

  const categoryName = categories.find((c) => c.id === team?.category_id)?.name;

  // --- Écritures au blur (édition) -------------------------------------
  // Un champ ne part que s'il passe SA validation : une adresse incomplète
  // reste sous le champ avec son erreur, rien n'est écrit.
  const saveField = async (name: 'name' | 'team_id' | 'contact_email' | 'contact_phone') => {
    if (!editing) return;
    if (!(await form.trigger(name))) return;
    const raw = form.getValues(name);
    const value = name === 'contact_phone' ? raw : raw.trim();
    const key = name === 'name' ? 'team_name' : name;
    const before = toValues(team)[name];
    await autoSave.commitIfChanged(key, value, () => form.setValue(name, before));
  };

  // Les participants partent en bloc : le serveur les range par position et
  // retire ceux qui ne sont plus dans la liste.
  const saveParticipants = async (revert?: () => void) => {
    if (!editing) return;
    if (!(await form.trigger('participants'))) return;
    const list = participantsPayload(form.getValues('participants'));
    const json = JSON.stringify(list);
    if (json === participantsBaseline.current) return;
    const previous = participantsBaseline.current;
    participantsBaseline.current = json;
    await autoSave.commit({ participants: list }, () => {
      participantsBaseline.current = previous;
      (revert ?? (() => form.setValue('participants', toValues(team).participants)))();
    });
  };

  const removeParticipant = async (index: number) => {
    const removed = form.getValues(`participants.${index}`);
    remove(index);
    if (!editing) return;
    await saveParticipants(() => insert(index, removed));
    const label = `${removed.first_name} ${removed.last_name}`.trim();
    toast.success(t('teams.form.participantRemoved', { name: label || t('teams.form.participant') }), {
      action: {
        label: t('common.undo'),
        onClick: () => { insert(index, removed); void saveParticipants(); },
      },
    });
  };

  const changeCategory = async (value: string) => {
    const next = value === 'none' ? '' : value;
    const before = form.getValues('category_id');
    if (next === before) return;
    form.setValue('category_id', next);
    if (!editing) return;
    const done = await onCategoryChange(next);
    if (!done) form.setValue('category_id', before);
  };

  return (
    <>
      <Sheet open={open} modal={false} onOpenChange={(o) => { if (!o) guard(onClose); }}>
        <SheetContent
          side="right"
          hideClose
          // Non modal : la liste reste utilisable derrière. Un clic dessus ne
          // ferme pas le panneau — il y ouvre une autre équipe (géré par la page).
          onInteractOutside={(e) => e.preventDefault()}
          className="flex w-full flex-col gap-0 p-0 shadow-2xl shadow-black/60 sm:max-w-[560px]"
        >
          <Form {...form}>
            <form onSubmit={form.handleSubmit(onSubmit)} className="flex h-full min-h-0 flex-col" noValidate>
              <div className="flex items-start gap-2 border-b border-white/[0.08] px-6 pb-4 pt-6">
                <div className="flex min-w-0 flex-1 flex-col gap-1.5">
                  <SheetDescription className="text-xs">
                    {team
                      ? [team.team_id && `N° ${team.team_id}`, categoryName, heatSummary].filter(Boolean).join(' · ')
                      : t('teams.form.createHint')}
                  </SheetDescription>
                  <SheetTitle className="truncate font-anton text-3xl font-normal uppercase leading-none">
                    {team ? team.name : t('teams.newTeam')}
                  </SheetTitle>
                </div>
                {team && (
                  <>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.previous')} disabled={!onPrev} onClick={() => onPrev && guard(onPrev)}>
                      <ChevronUp size={16} />
                    </Button>
                    <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('teams.form.next')} disabled={!onNext} onClick={() => onNext && guard(onNext)}>
                      <ChevronDown size={16} />
                    </Button>
                  </>
                )}
                <Button type="button" variant="ghost" size="icon" className="h-9 w-9" aria-label={t('common.close')} onClick={() => guard(onClose)}>
                  <X size={16} />
                </Button>
              </div>

              <div className="min-h-0 flex-1 space-y-7 overflow-y-auto px-6 py-6">
                <section className="space-y-4">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('teams.form.sectionTeam')}</h3>
                  <div className="grid grid-cols-[1fr,120px] gap-3">
                    <FormField control={form.control} name="name" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('teams.teamName')}</FormLabel>
                        <FormControl>
                          <Input {...field} onBlur={() => { field.onBlur(); void saveField('name'); }} placeholder={t('teams.teamNamePlaceholder')} autoFocus={!team} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="team_id" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('teams.teamId')}</FormLabel>
                        <FormControl>
                          <Input {...field} onChange={(e) => field.onChange(e.target.value.toUpperCase())} onBlur={() => { field.onBlur(); void saveField('team_id'); }} placeholder={t('teams.teamIdPlaceholder')} />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                  <FormField control={form.control} name="category_id" render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('teams.category')}</FormLabel>
                      <Select value={field.value || 'none'} onValueChange={(v) => { if (v) void changeCategory(v); }}>
                        <FormControl>
                          <SelectTrigger><SelectValue placeholder={t('teams.noCategory')} /></SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">{t('teams.noCategory')}</SelectItem>
                          {categories.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                      {team?.registration && (
                        <FormDescription>{t('teams.form.categoryPaidHint')}</FormDescription>
                      )}
                    </FormItem>
                  )} />
                </section>

                <section className="space-y-4">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">{t('teams.form.sectionContact')}</h3>
                  <div className="grid gap-3 sm:grid-cols-2">
                    <FormField control={form.control} name="contact_email" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('teams.contactEmail')}</FormLabel>
                        <FormControl><Input type="email" {...field} onBlur={() => { field.onBlur(); void saveField('contact_email'); }} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                    <FormField control={form.control} name="contact_phone" render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('teams.contactPhone')}</FormLabel>
                        <FormControl><Input {...field} onBlur={() => { field.onBlur(); void saveField('contact_phone'); }} /></FormControl>
                        <FormMessage />
                      </FormItem>
                    )} />
                  </div>
                </section>

                <section className="space-y-3">
                  <h3 className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                    {t('teams.form.sectionParticipants', { count: fields.length })}
                  </h3>
                  {fields.map((row, index) => (
                    <div key={row.id} className="space-y-2.5 border-t border-white/[0.08] pt-3">
                      <div className="flex items-start gap-2">
                        <div className="grid flex-1 grid-cols-2 gap-2.5">
                          <FormField control={form.control} name={`participants.${index}.first_name`} render={({ field }) => (
                            <FormItem>
                              <FormControl><Input {...field} onBlur={() => { field.onBlur(); void saveParticipants(); }} aria-label={t('common.firstName')} placeholder={t('common.firstName')} /></FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                          <FormField control={form.control} name={`participants.${index}.last_name`} render={({ field }) => (
                            <FormItem>
                              <FormControl>
                                <Input {...field} onBlur={() => { field.onBlur(); void saveParticipants(); }} aria-label={t('common.lastName')} placeholder={t('common.lastName')} />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )} />
                        </div>
                        {fields.length > 1 && (
                          <Button type="button" variant="ghost" size="icon" className="h-10 w-10 shrink-0 text-muted-foreground hover:text-destructive" aria-label={t('teams.form.removeParticipant')} onClick={() => void removeParticipant(index)}>
                            <Trash2 size={16} />
                          </Button>
                        )}
                      </div>
                      <div className="grid grid-cols-2 gap-2.5">
                        <FormField control={form.control} name={`participants.${index}.email`} render={({ field }) => (
                          <FormItem>
                            <FormControl>
                              <Input
                                type="email"
                                {...field}
                                aria-label={t('common.email')}
                                placeholder={t('common.email')}
                                onChange={(e) => {
                                  // Le 1er participant sert d'email de contact tant qu'on
                                  // n'en a pas saisi un autre (comportement d'avant).
                                  const contact = form.getValues('contact_email');
                                  if (index === 0 && (!contact || contact === field.value)) {
                                    form.setValue('contact_email', e.target.value, { shouldDirty: true });
                                  }
                                  field.onChange(e.target.value);
                                }}
                                onBlur={() => { field.onBlur(); void saveParticipants(); void saveField('contact_email'); }}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )} />
                        <FormField control={form.control} name={`participants.${index}.phone`} render={({ field }) => (
                          <FormItem>
                            <FormControl><Input {...field} onBlur={() => { field.onBlur(); void saveParticipants(); }} aria-label={t('common.phone')} placeholder={t('common.phone')} /></FormControl>
                          </FormItem>
                        )} />
                      </div>
                    </div>
                  ))}
                  {/* Une ligne vide n'est pas envoyée : le participant s'écrit dès qu'il a un nom et un email. */}
                  <Button type="button" variant="ghost" size="sm" className="border border-dashed border-white/[0.16] text-muted-foreground" onClick={() => append({ ...emptyParticipant })}>
                    <Plus size={16} />
                    {t('teams.addParticipant')}
                  </Button>
                </section>

                {serverError && (
                  <Alert variant="destructive">
                    <AlertDescription>{serverError}</AlertDescription>
                  </Alert>
                )}
              </div>

              <div className="flex items-center gap-2 border-t border-white/[0.08] px-6 py-4">
                <span className="flex flex-1 items-center gap-2 text-sm text-muted-foreground" aria-live="polite">
                  {editing
                    ? <AutoSaveIndicator status={autoSave.status} retry={autoSave.retry} />
                    : isDirty && (
                      <>
                        <span aria-hidden className="h-1.5 w-1.5 rounded-full bg-warning" />
                        {t('teams.form.unsaved', { count: Math.max(Object.keys(form.formState.dirtyFields).length, 1) })}
                      </>
                    )}
                </span>
                {editing ? (
                  <Button type="button" variant="ghost" onClick={onClose}>{t('common.close')}</Button>
                ) : (
                  <>
                    <Button type="button" variant="ghost" onClick={() => guard(onClose)} disabled={saving}>{t('common.cancel')}</Button>
                    <Button type="submit" disabled={saving}>
                      {saving && <Loader2 className="animate-spin" />}
                      {t('common.create')}
                    </Button>
                  </>
                )}
              </div>
            </form>
          </Form>
        </SheetContent>
      </Sheet>

      <ConfirmDialog
        open={confirmDiscard !== null}
        onOpenChange={(o) => { if (!o) setConfirmDiscard(null); }}
        title={t('teams.form.discardTitle')}
        message={t('teams.form.discardMessage')}
        confirmLabel={t('teams.form.discardConfirm')}
        destructive
        onConfirm={() => { const next = confirmDiscard; setConfirmDiscard(null); form.reset(toValues(team)); next?.(); }}
      />
    </>
  );
}
